// src/admin/AdminLogin.jsx
import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

const UI = {
  spacing: { xs: 6, sm: 8, md: 12, lg: 16, xl: 24 },
  radius: { sm: 6, md: 8, lg: 12 },
  colors: {
    primary: "#0F172A",
    secondary: "#334155",
    muted: "#64748B",
    border: "#E2E8F0",
    bg: "#F8FAFC",
    success: "#059669",
    warning: "#D97706",
    danger: "#DC2626",
    info: "#3B82F6"
  }
};

export default function AdminLogin({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password
      });

      if (signInError) throw signInError;

      if (data?.user) {
        // Check if user has admin role
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', data.user.id)
          .single();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          setError('Unable to verify admin status');
          await supabase.auth.signOut();
          setLoading(false);
          return;
        }

        if (profile?.role === 'admin') {
          showToast('Login successful! Redirecting...', 'success');
          setTimeout(() => {
            if (onLoginSuccess) onLoginSuccess(data.user);
            window.location.href = '/admin/categories';
          }, 1000);
        } else {
          setError('Admin privileges required');
          await supabase.auth.signOut();
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Failed to login');
      showToast(error.message || 'Failed to login', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: UI.colors.bg,
      padding: UI.spacing.xl
    }}>
      <div style={{
        maxWidth: 440,
        width: '100%',
        background: '#FFFFFF',
        borderRadius: UI.radius.lg,
        boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: `${UI.spacing.xl}px ${UI.spacing.xl}px ${UI.spacing.md}px ${UI.spacing.xl}px`,
          textAlign: 'center',
          borderBottom: `1px solid ${UI.colors.border}`
        }}>
          <div style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 24,
            fontWeight: 700,
            color: UI.colors.primary,
            marginBottom: UI.spacing.xs
          }}>
            Admin Access
          </div>
          <div style={{ fontSize: 13, color: UI.colors.muted }}>
            Sign in to manage the platform
          </div>
        </div>

        {/* Login Form */}
        <div style={{ padding: UI.spacing.xl }}>
          <form onSubmit={handleEmailLogin}>
            <div style={{ marginBottom: UI.spacing.lg }}>
              <label style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 600,
                marginBottom: UI.spacing.sm,
                color: UI.colors.secondary
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                style={{
                  width: '100%',
                  padding: UI.spacing.md,
                  border: `1px solid ${UI.colors.border}`,
                  borderRadius: UI.radius.md,
                  fontSize: 14,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  boxSizing: 'border-box'
                }}
                autoComplete="email"
              />
            </div>

            <div style={{ marginBottom: UI.spacing.lg }}>
              <label style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 600,
                marginBottom: UI.spacing.sm,
                color: UI.colors.secondary
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{
                  width: '100%',
                  padding: UI.spacing.md,
                  border: `1px solid ${UI.colors.border}`,
                  borderRadius: UI.radius.md,
                  fontSize: 14,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  boxSizing: 'border-box'
                }}
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div style={{
                padding: UI.spacing.md,
                background: '#FEE2E2',
                border: `1px solid ${UI.colors.danger}`,
                borderRadius: UI.radius.md,
                marginBottom: UI.spacing.lg,
                fontSize: 13,
                color: UI.colors.danger,
                textAlign: 'center'
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: UI.spacing.md,
                background: UI.colors.primary,
                border: 'none',
                borderRadius: UI.radius.md,
                fontSize: 14,
                fontWeight: 600,
                color: '#FFFFFF',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div style={{
            marginTop: UI.spacing.lg,
            paddingTop: UI.spacing.lg,
            borderTop: `1px solid ${UI.colors.border}`,
            fontSize: 12,
            color: UI.colors.muted,
            textAlign: 'center'
          }}>
            Secure admin access only
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: toast.type === 'error' ? UI.colors.danger : UI.colors.success,
          color: '#FFFFFF',
          padding: `${UI.spacing.md}px ${UI.spacing.lg}px`,
          borderRadius: UI.radius.md,
          fontSize: 13,
          fontWeight: 600,
          zIndex: 999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}